<script setup>
import { reactive, computed, watch, getCurrentInstance } from 'vue';
import { Countries } from '/src/app/features/shared/libraries/countries.js';

const props = defineProps({
  selectedCountry: {
    type: String,
    required: true
  }
});

const { proxy } = getCurrentInstance();
const { $bus, $constants, $stores } = proxy;

const sourceData = computed(() => $stores.appData?.state?.data?.rates || {});

const form = reactive({
  buyRate: 0,
  sellRate: 0,
  currency: 'XOF'
});

watch(
  [sourceData, () => props.selectedCountry],
  ([data, country]) => {
    if (data && data[country]) {
      const countryInfo = Countries.find(country);
      const currency = countryInfo?.currency || 'XOF';
      form.buyRate = data[country].buyRates?.USDT?.[currency] || 0;
      form.sellRate = data[country].sellRates?.USDT?.[currency] || 0;
      form.currency = currency;
    }
  },
  { immediate: true, deep: true }
);

const hasChanges = computed(() => {
  const currency = form.currency || 'XOF';
  return (
    form.buyRate !== (sourceData.value?.[props.selectedCountry]?.buyRates?.USDT?.[currency] || 0) ||
    form.sellRate !== (sourceData.value?.[props.selectedCountry]?.sellRates?.USDT?.[currency] || 0)
  );
});

function save() {
  if (!hasChanges.value) return;

  const currency = form.currency || 'XOF';
  const payload = { rates: {} };
  
  payload.rates[props.selectedCountry] = {
    buyRates: {
      USDT: { [currency]: form.buyRate }
    },
    sellRates: {
      USDT: { [currency]: form.sellRate }
    }
  };

  $bus.emit($constants.EVENTS.APP_DATA.UPDATE_RATES, payload);
}

function reset() {
  const currency = form.currency || 'XOF';
  if (sourceData.value?.[props.selectedCountry]) {
    form.buyRate = sourceData.value[props.selectedCountry].buyRates?.USDT?.[currency] || 0;
    form.sellRate = sourceData.value[props.selectedCountry].sellRates?.USDT?.[currency] || 0;
  }
}
</script>

<template>
  <div class="bg-white rounded-2xl shadow-sm p-6 border border-gray-100 space-y-6">
    <!-- HEADER -->
    <div class="flex justify-between items-center pb-4 border-b border-gray-100">
      <div class="flex items-center gap-3">
        <span class="text-2xl">💱</span>
        <div>
          <h3 class="text-lg font-bold text-gray-800">Taux de change USDT</h3>
          <p class="text-sm text-gray-500">Devise locale : {{ form.currency }} ({{ Countries.find(props.selectedCountry)?.countryName }})</p>
        </div>
      </div>
      <div class="flex gap-3" v-show="hasChanges">
        <button
          class="px-4 py-2 rounded-xl bg-gray-100 text-gray-600 font-medium hover:bg-gray-200 transition-all"
          @click="reset"
        >
          Annuler
        </button>
        <button
          class="px-4 py-2 rounded-xl bg-primary-600 text-white font-medium hover:bg-primary-700 shadow-lg shadow-primary-100 transition-all"
          @click="save"
        >
          Sauvegarder
        </button>
      </div>
    </div>

    <!-- FIELDS -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
      <!-- BUY RATE -->
      <div class="space-y-2">
        <label class="block text-sm font-semibold text-gray-600 uppercase tracking-wider ml-1">
          Achat USDT/{{ form.currency }}
        </label>
        <div class="relative group">
          <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-green-500 font-bold text-lg group-focus-within:scale-110 transition-transform">
            📥
          </div>
          <input
            type="number"
            class="inputComponent !pl-12 !py-4 !text-xl !font-bold"
            v-model.number="form.buyRate"
          />
        </div>
        <p class="text-[10px] text-gray-400 font-medium ml-1">Prix auquel vous achetez l'USDT aux clients.</p>
      </div>

      <!-- SELL RATE -->
      <div class="space-y-2">
        <label class="block text-sm font-semibold text-gray-600 uppercase tracking-wider ml-1">
          Vente USDT/{{ form.currency }}
        </label>
        <div class="relative group">
          <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-amber-500 font-bold text-lg group-focus-within:scale-110 transition-transform">
            📤
          </div>
          <input
            type="number"
            class="inputComponent !pl-12 !py-4 !text-xl !font-bold"
            v-model.number="form.sellRate"
          />
        </div>
        <p class="text-[10px] text-gray-400 font-medium ml-1">Prix auquel vous vendez l'USDT aux clients.</p>
      </div>
    </div>
  </div>
</template>
